import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "sonner";
import arrowForward from "../../../../assets/images/arrow_forward_button.svg";
import exclamationIcon from "../../../../assets/images/Exclamation.svg";
import useAmanFlow from "../../../../hooks/useAmanFlow";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { 
  selectAmanForm, 
  selectAmanQuoteResponse 
} from "../../../../redux/features/amanFormSlice";
import { selectNationalities } from "../../../../redux/features/constants/nationalitiesSlice";
import { policyPay, getPolicyPayLoading } from "../../../../redux/features/payment/policyPaySlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  applyPromoCodeToQuote,
  calculatePromoDiscount,
} from "../../../../utils/promoCodeUtils";
import Button from "../../../common/Button/Button";
import SummaryContent from "../../../common/SummaryContent/SummaryContent";
import AmanJourneyLayout from "../../layouts/AmanJourneyLayout";

const ReviewAndConfirmPage = ({ onClose }) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const { handlePrevSubstep } = useAmanFlow();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  const amanFormData = useSelector(selectAmanForm);
  const nationalityOptions = useSelector(selectNationalities);
  const quoteResponse = useSelector(selectAmanQuoteResponse);
  const policyPayLoading = useSelector(getPolicyPayLoading);

  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const helperData = amanFormData?.AMAN_DOMESTIC_HELPER_FORM || {};
  const planData = amanFormData?.AMAN_PLAN_SELECTION || {};

  const selectedPlanName =
    planData.planId === "aman_plus"
      ? translate("Aman Plus", "أمان بلس")
      : translate("Aman", "أمان");

  const quotePremium = quoteResponse?.premium || quoteResponse?.data?.Premium || 0;
  const quoteTax = quoteResponse?.tax || quoteResponse?.data?.Tax || 0;
  const quoteTotalPremium = quoteResponse?.totalPremium || quoteResponse?.data?.TotalPremium || 0;
  const quoteNumber = quoteResponse?.quoteNumber || 
                      quoteResponse?.data?.PolicyNumber || 
                      quoteResponse?.data?.QuoteDetails?.QuoteNumber || 
                      quoteResponse?.data?.QuoteNumber || 
                      "";

  const apiPremium = parseFloat(quotePremium) || 0;
  const apiTax = parseFloat(quoteTax) || 0;
  const apiTotalPremium = parseFloat(quoteTotalPremium) || 0;

  const getNationalityLabel = (nationalityCode) => {
    if (!nationalityCode) return "-";
    const nationality = nationalityOptions.find(
      (n) => n.code === nationalityCode || n.value === nationalityCode
    );
    return nationality?.label || nationalityCode;
  };

  const formatDateOfBirth = (dob) => {
    if (!dob || !dob.day || !dob.month || !dob.year) return "-";
    return `${dob.day}/${dob.month}/${dob.year}`;
  };

  const getGenderLabel = (gender) => {
    if (!gender) return "-";
    if (gender === "male") return translate("Male", "ذكر");
    if (gender === "female") return translate("Female", "أنثى");
    return gender;
  };

  const summaryPlanData = {
    premium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : apiPremium,
    tax: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Tax
      : apiTax,
    totalPremium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.TotalPremium
      : apiTotalPremium,
    quoteNumber: quoteNumber || "Pending",
    basePremium: promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.Premium
      : apiPremium,
  };

  const applyPromoCode = async () => {
    setIsApplyingPromo(true);

    try {
      const result = await applyPromoCodeToQuote(quoteNumber, promoCode, translate);

      if (result.success) {
        const discount = calculatePromoDiscount(apiTotalPremium, result.data.TotalPremium);

        setPromoCodeApplied(true);
        setPromoCodeDiscount(discount);
        setPromoAppliedQuoteData(result.data);

        toast.success(
          translate(
            "Promo code applied!",
            "تم تطبيق كود الخصم!"
          )
        );
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Promo code application exception:', error);
      toast.error(
        translate(
          "Failed to apply promo code. Please try again.",
          "فشل تطبيق كود الخصم. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
  };

  const handleProceedToPayment = async () => {
    if (!agreedToTerms) {
      toast.error(
        translate(
          "Please agree to the terms and conditions",
          "يرجى الموافقة على الشروط والأحكام"
        )
      );
      return;
    }

    if (!quoteNumber || !apiTotalPremium) {
      toast.error(
        translate(
          "Unable to process payment. Quote information is missing.",
          "غير قادر على معالجة الدفع. معلومات التسعيرة مفقودة."
        )
      );
      console.error("Missing payment data:", {
        quoteNumber,
        totalPremium: apiTotalPremium,
      });
      return;
    }

    sessionStorage.setItem("isRedirectingToPayment", "true");

    const finalQuoteNumber = promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.PolicyNumber
      : quoteNumber;

    const finalPremium = promoCodeApplied && promoAppliedQuoteData
      ? promoAppliedQuoteData.TotalPremium
      : apiTotalPremium;

    console.log('=== Payment Preparation ===');
    console.log('Promo Code Applied:', promoCodeApplied);
    console.log('Original Quote Number:', quoteNumber);
    console.log('Final Quote Number (for payment):', finalQuoteNumber);
    console.log('Original Premium:', apiTotalPremium);
    console.log('Final Premium (for payment):', finalPremium);

    const payload = {
      PolicyNumber: finalQuoteNumber,
      Premium: finalPremium,
      Channel: getClientSource(),
    };

    setIsLoading(true);

    try {
      console.log("Initiating payment with payload:", payload);

      const linkAction = await dispatch(policyPay(payload));
      const link = unwrapResult(linkAction);

      console.log("Payment link received:", link);

      if (link) {
        window.location.href = link;
      } else {
        sessionStorage.removeItem("isRedirectingToPayment");
        toast.error(
          translate(
            "Unable to generate payment link. Please try again.",
            "غير قادر على إنشاء رابط الدفع. يرجى المحاولة مرة أخرى."
          )
        );
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      sessionStorage.removeItem("isRedirectingToPayment");
      toast.error(
        translate(
          "Payment failed. Please try again.",
          "فشل الدفع. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const amanDetailsData = {
    helperName: helperData.fullName || null,
    nationality: getNationalityLabel(helperData.nationality),
    cprNumber: helperData.cprNumber || null,
    dateOfBirth: formatDateOfBirth(helperData.dateOfBirth),
    gender: getGenderLabel(helperData.gender),
    plan: selectedPlanName,
  };

  return (
    <div>
      <AmanJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
        onClose={onClose}
      >
        <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 max767:pt-2 py-5 w-full pb-0 max767:pb-0">
          <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-2xl">
            {/* Header */}
            <div className="text-center max767:text-left max767:rtl:text-right mb-8">
              <h1 className="text-2xl font-semibold text-[#1A2024] mb-2 text-center max767:text-left max767:rtl:text-right">
                {translate("Review and Confirm", "المراجعة والتأكيد")}
              </h1>
              <p className="text-gray-600 text-center max767:text-left max767:rtl:text-right">
                {translate(
                  "Please review your information and proceed to payment.",
                  "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                )}
              </p>
            </div>

            {/* Policy Section */}
            <div className="mb-8">
              {/* Aman Details Table */}
              <div className="bg-[#F6F8F9] max767:bg-white rounded-xl p-4 max767:p-0 mb-6">
                <h2 className="text-base font-semibold text-[#1A2024] mb-2">
                  {translate(
                    "New Aman Insurance Policy",
                    "وثيقة تأمين أمان جديدة"
                  )}
                </h2>
                <p className="text-sm mb-4 text-[#1A2024]">
                  {translate(
                    "Review the details below and confirm the payment",
                    "قم بمراجعة التفاصيل أدناه وتأكيد الدفع"
                  )}
                </p>

                {/* Aman Details Table - with proper border separator */}
                <div className="bg-white border border-[#E5E7EB] rounded-[10px] overflow-hidden">
                  {amanDetailsData.helperName && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 py-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Helper Name", "اسم العاملة")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 py-4">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {amanDetailsData.helperName}
                        </span>
                      </div>
                    </div>
                  )}
                  {amanDetailsData.nationality && amanDetailsData.nationality !== "-" && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 py-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Nationality", "الجنسية")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 py-4">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {amanDetailsData.nationality}
                        </span>
                      </div>
                    </div>
                  )}
                  {amanDetailsData.cprNumber && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 py-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("CPR Number", "الرقم السكاني")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 py-4">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {amanDetailsData.cprNumber}
                        </span>
                      </div>
                    </div>
                  )}
                  {amanDetailsData.dateOfBirth && amanDetailsData.dateOfBirth !== "-" && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 py-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Date of birth", "تاريخ الميلاد")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 py-4">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {amanDetailsData.dateOfBirth}
                        </span>
                      </div>
                    </div>
                  )}
                  {amanDetailsData.gender && amanDetailsData.gender !== "-" && (
                    <div className="flex border-b border-[#E5E7EB]">
                      <div className="flex-1 px-4 py-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0">
                        <span className="text-[#6E7C87] text-sm font-medium">
                          {translate("Gender", "الجنس")}
                        </span>
                      </div>
                      <div className="flex-1 bg-white px-4 py-4">
                        <span className="text-[#1A2024] text-sm font-medium">
                          {amanDetailsData.gender}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex">
                    <div className="flex-1 px-4 py-4 bg-white border-r border-[#E5E7EB] rtl:border-l rtl:border-r-0">
                      <span className="text-[#6E7C87] text-sm font-medium">
                        {translate("Plan", "الخطة")}
                      </span>
                    </div>
                    <div className="flex-1 bg-white px-4 py-4">
                      <span className="text-[#1A2024] text-sm font-medium">
                        {amanDetailsData.plan}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <div className="bg-[#F6F8F9] rounded-xl p-4">
                <SummaryContent
                  planData={summaryPlanData}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  applyPromoCode={applyPromoCode}
                  clearPromoCode={clearPromoCode}
                  promoCodeApplied={promoCodeApplied}
                  promoCodeDiscount={promoCodeDiscount}
                  isApplyingPromo={isApplyingPromo}
                  showQuotationNumber={true}
                  showPromoCode={true}
                  className="!bg-transparent"
                  flowType="aman"
                  showNonRefundableNotice={true}
                />
              </div>
            </div>

            {/* Policy Notice */}
            <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-center gap-2">
              <img src={exclamationIcon} alt="" className="w-6 h-6 flex-shrink-0" />
              <p className="text-[#252C32] font-semibold text-sm">
                {translate(
                  "Kindly note that the policy will be issued under your name for the Domestic Helper.",
                  "يرجى ملاحظة أن الوثيقة ستصدر باسمك لعامل المنزل."
                )}
              </p>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-6">
              <div className="flex gap-2 items-start py-1">
                <div className="pt-[2px] shrink-0">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="peer hidden"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    <div
                      className="size-5 border-2 border-gray-400 rounded-[4px] 
                peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7] 
                flex items-center justify-center transition-all"
                    >
                      <svg
                        width="12"
                        height="9"
                        viewBox="0 0 12 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </label>
                </div>
                <div className={`flex-1 text-[14px] font-normal text-[#252C32] leading-[24px] ${isRTL ? "text-right" : "text-left"}`}>
                  {isRTL ? (
                    <>
                      <p className="mb-[12px]">
                        بصفتي الكفيل وطالب التأمين الصحي للعمالة المنزلية, أقر أنا الموقع أدناه بأن المعلومات المذكورة أعلاه صحيحة ودقيقة وكاملة.
                      </p>
                      <p className="mb-[12px]">
                        أوافق على الالتزام بالشروط والأحكام ،ومن المتفق عليه أيضا أن هذا الطلب والمعلومات المقدمة فيه تشكل أساس العقد بين مقدم الطلب (الكفيل) والعامل المنزلي، وشركة سوليدرتي البحرين ش.م.ب.
                      </p>
                      <p className="mb-[12px]">
                        بعد قراءة جميع الشروط والاحكام والوثائق المقدمة لى، أنا مقتنع بأن التغطية المختارة تلبي متطلباتي في الوقت الحالي.
                      </p>
                      <p>
                        كما أفوض شركة سوليدرتي البحرين تقديم تفاصيل هذه الوثيقة عند طلبها من قبل الجهات القانونية وفقاً للأنظمة والقوانين النافذة و خصوصاً ما يتعلق بحاملي الجنسية الأمريكية والمادة ١١٧ (١) من قانون مصرف البحرين المركزي.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-[12px]">
                        I, the undersigned hereby apply to cover the Domestic Helper named in this Application to the best of my knowledge and belief the information given in this Application is true and complete.
                      </p>
                      <p className="mb-[12px]">
                        I agree to be bound by the Terms & Conditions. It is agreed this declaration and information supplied in this Application shall form the basis of the Contract between me, my Domestic Helper, and Solidarity Bahrain B.S.C.
                      </p>
                      <p className="mb-[12px]">
                        After reading all the Terms & Conditions and documents provided to me, I am satisfied that the Plan selected meets my requirements at this time.
                      </p>
                      <p>
                        I Also, authorize Solidarity Bahrain to provide details of this Policy on request by a legal authority in accordance with the laws and regulations in force and in particular for US Citizens and Article 117 (1) of the legislation of Central Bank of Bahrain.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Proceed to Payment Button - Desktop */}
            <div className="max767:hidden">
              <Button
                onSubmit={handleProceedToPayment}
                disabled={!agreedToTerms || isLoading || policyPayLoading}
                isLoading={isLoading || policyPayLoading}
                label={translate("Proceed to Payment", "انتقل إلى الدفع")}
                post_icon={arrowForward}
                post_icon_class={isRTL ? "rotate-180 gap-2" : ""}
                className="w-full mt-6 py-4 px-6 rounded-[10px] gap-2"
              />
            </div>
          </div>
        </div>

        {/* Proceed to Payment Button - Mobile (Fixed at Bottom) */}
        <div className="hidden max767:block fixed bottom-0 left-0 w-full bg-white border-t border-[#E5E9EB] p-4 pb-6 z-[9] max767:pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button
            onSubmit={handleProceedToPayment}
            disabled={!agreedToTerms || isLoading || policyPayLoading}
            isLoading={isLoading || policyPayLoading}
            label={translate("Proceed to Payment", "انتقل إلى الدفع")}
            fontSize="text-lg max767:text-sm"
            post_icon={arrowForward}
            post_icon_class={isRTL ? "rotate-180" : ""}
            className="w-full py-4 px-6 rounded-[10px] max767:h-[40px] max767:p-0 max767:justify-center"
          />
        </div>
      </AmanJourneyLayout>
    </div>
  );
};

export default ReviewAndConfirmPage;
